/**
 * Plain-date helpers for the API's IST `YYYY-MM-DD` strings. Arithmetic runs on
 * UTC midnights so the browser's own timezone can never shift a day, which
 * `new Date('2026-09-03')` followed by local getters would.
 */

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
const MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAY = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_MS = 864e5;
const IST_OFFSET_MS = 330 * 60 * 1000;

const pad2 = (n: number) => String(n).padStart(2, '0');

export const parts = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return { y, m, d };
};

const toMs = (iso: string) => {
  const { y, m, d } = parts(iso);
  return Date.UTC(y, m - 1, d);
};

const fromMs = (ms: number) => {
  const dt = new Date(ms);
  return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;
};

export const isIsoDate = (s: unknown): s is string => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);

export const addDays = (iso: string, n: number) => fromMs(toMs(iso) + n * DAY_MS);

/** Whole days from `a` to `b`; positive when `b` is later. */
export const diffDays = (a: string, b: string) => Math.round((toMs(b) - toMs(a)) / DAY_MS);

/** 0 = Sunday … 6 = Saturday. */
export const weekday = (iso: string) => new Date(toMs(iso)).getUTCDay();

export const mondayOf = (iso: string) => addDays(iso, -((weekday(iso) + 6) % 7));

export const minIso = (a: string, b: string) => (a <= b ? a : b);

/** Today's calendar date in India, whatever the device's timezone. */
export const todayIst = (now: number = Date.now()) => fromMs(now + IST_OFFSET_MS);

/** "5 Aug" */
export const fmtShort = (iso: string) => {
  const { m, d } = parts(iso);
  return `${d} ${MONTH_SHORT[m - 1]}`;
};

/** "Thursday 3 Sept" */
export const fmtLong = (iso: string) => `${WEEKDAY[weekday(iso)]} ${fmtShort(iso)}`;

export const monthShort = (iso: string) => MONTH_SHORT[parts(iso).m - 1];

/** "October 2026" */
export const monthYear = (iso: string) => {
  const { y, m } = parts(iso);
  return `${MONTH_LONG[m - 1]} ${y}`;
};

export const weekdayInitial = (iso: string) => WEEKDAY[weekday(iso)][0];
